import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Paper,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

interface PrivacyPolicyProps {
  open: boolean;
  onClose: () => void;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1,
        borderBottom: '1px solid #eee'
      }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
          Privacy Policy
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

             <DialogContent sx={{ py: 3 }}>
         <Box sx={{ maxWidth: '100%' }}>
           <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
             <strong>Effective Date:</strong> 1 June 2025
           </Typography>

           <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6 }}>
             This Privacy Policy outlines how we collect, use, and protect your data when you use Gamura, our AI-powered document assistant.
           </Typography>

           <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
             1. What We Collect
           </Typography>
           <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
             <strong>Account Data:</strong> Email address and login credentials (if applicable).
           </Typography>
           <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
             <strong>Uploaded Documents:</strong> Files you upload for question-answering and processing.
           </Typography>
           <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
             <strong>Queries:</strong> Questions you ask, including text inputs.
           </Typography>
           <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6 }}>
             <strong>Usage Logs:</strong> Basic analytics and system logs for performance monitoring.
           </Typography>

           <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
             2. How We Use Your Data
           </Typography>
           <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
             • To provide answers based on your uploaded documents.
           </Typography>
           <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
             • To improve system performance and stability.
           </Typography>
           <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6 }}>
             • For support and troubleshooting if issues arise.
           </Typography>

           <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
             3. Data Ownership & Access
           </Typography>
           <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
             • You retain full ownership of your documents and queries.
           </Typography>
           <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
             • We do not use your data to train AI models.
           </Typography>
           <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6 }}>
             • No third parties access your content unless required by law.
           </Typography>

           <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
             4. Security Measures
           </Typography>
           <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
             • Data is encrypted at rest and in transit.
           </Typography>
           <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
             • Only you (and your authorized team, if applicable) have access to your uploaded content.
           </Typography>
           <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6 }}>
             • We implement access control and regular security checks.
           </Typography>

           <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
             5. Data Retention
           </Typography>
           <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
             • Uploaded content may be temporarily cached for faster response, then deleted.
           </Typography>
           <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6 }}>
             • You may request full deletion of your data at any time.
           </Typography>

           <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
             6. Contact Us
           </Typography>
           <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6 }}>
             For questions or data requests, contact:
           </Typography>
           <Paper sx={{ p: 2, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
             <Typography variant="body2" sx={{ color: '#444' }}>
               <strong>Email:</strong> support@gamura.ai
             </Typography>
           </Paper>
         </Box>
       </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee' }}>
        <Button 
          onClick={onClose} 
          variant="contained"
          sx={{ 
            bgcolor: '#1a1a1a',
            color: '#fff',
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              bgcolor: '#333',
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrivacyPolicy; 